import React from 'react';
import PropTypes from 'prop-types';
import GeckoRequest from 'services/gecko-request';
import GeckoLocation from 'services/gecko-location';
import { useSelector } from 'react-redux';
import {
  GECKO_CONSTS,
  GeckoFormField,
  GeckoForm,
  GeckoModal,
  GeckoModalService,
  GeckoToastService,
} from 'ui';
import { renderError } from 'services/gecko-error';

const DataSourceModal = ({ dataSource = {}, onSave = () => {}, ...props }) => {
  const isNew = dataSource.id === undefined;

  const initialValues = {
    name: dataSource.name || '',
    type: dataSource.type || '',
    mail_merge_tag: dataSource.mail_merge_tag || '',
    ...dataSource,
  };
  const dataSources = useSelector((state) => state.data_sources);

  const isUniqueTag = (tag) => {
    return !dataSources.some((source) => {
      return (
        source.mail_merge_tag === tag && (isNew || source.id !== dataSource.id)
      );
    });
  };
  const isUniqueName = (name) => {
    return !dataSources.some((source) => {
      return source.name === name && (isNew || source.id !== dataSource.id);
    });
  };

  const validate = ({ name, type, mail_merge_tag }) => {
    const errors = {};

    const nameAlreadyExists = !isUniqueName(name);
    const tagAlreadyExists = !isUniqueTag(mail_merge_tag);

    if (!name || name.trim().length === 0) {
      errors.name = 'Please provide a name. Name can not be blank.';
    }

    if (nameAlreadyExists) {
      errors.name =
        'Sorry, this name is already taken. Please provide  another name.';
    }
    if (!type) {
      errors.type = 'Please select a type.';
    }

    const mailMergeTagRegex = new RegExp('^[a-zA-Z0-9_]*$');
    if (!mailMergeTagRegex.test(mail_merge_tag)) {
      errors.mail_merge_tag =
        'Please provide only alphanumeric characters or underscores.';
    }

    if (!mail_merge_tag || mail_merge_tag.trim().length === 0) {
      errors.mail_merge_tag =
        'Please provide a mail merge tag. Mail merge tag can not be blank.';
    }

    if (tagAlreadyExists) {
      errors.mail_merge_tag =
        'Sorry, this mail merge tag is already taken. Please provide  another mail merge tag.';
    }
    return errors;
  };

  const submit = (dataSource) => {
    const url = isNew
      ? '/models/data_sources'
      : `/models/data_sources/${dataSource.id}`;

    return GeckoRequest.post(url, dataSource)
      .then((dataSource) => {
        new GeckoToastService().success('Your changes have been saved.');
        GeckoLocation.reload();

        onSave(dataSource);
        new GeckoModalService().close();
        return GeckoLocation.goTo(`/settings/datasources/${dataSource.id}`);
      })
      .catch(renderError);
  };

  return (
    <GeckoForm
      initialValues={initialValues}
      onSubmit={submit}
      validate={validate}
    >
      {({ values, handleSubmit, errors }) => (
        <GeckoModal
          name={isNew ? 'Create New Data Source' : 'Edit Data Source'}
          btns={[
            {
              type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
              name: 'Done',
              icon: 'far fa-check',
              handler: handleSubmit,
            },
          ]}
        >
          <div className="DataSourceForm">
            <GeckoFormField
              fieldType="text"
              name="name"
              label="Name"
              description="Give the data source a descriptive name"
              placeholder="Enter a name"
              required
            />
            <GeckoFormField
              fieldType="select"
              name="type"
              options={[{ label: 'Table', value: 'table' }]}
              label="Type"
              value="table"
              disabled={!isNew}
              required
            />
            <GeckoFormField
              fieldType="text"
              name="mail_merge_tag"
              label="Mail Merge Tag"
              placeholder="Enter a mail merge tag"
              description="Customise the text used for the mail merge tag within messages. Please note, this value cannot be changed once the data source has been created."
              disabled={!isNew}
              required
            />
          </div>
        </GeckoModal>
      )}
    </GeckoForm>
  );
};

DataSourceModal.propTypes = {
  dataSource: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    mail_merge_tag: PropTypes.string,
  }),
  onSave: PropTypes.func,
};

export default DataSourceModal;
